import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  Modal,
  ModalDialog,
  ModalClose,
} from '@mui/joy';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { localSEOError } from '../../../lib/localSEOUtils';
import type {
  RankingKeyword,
  RankingScanSummary,
  RankingScanDetail,
} from '../../../types/marketing';
import { KeywordSelector } from './KeywordSelector';
import { RankingMap } from './RankingMap';
import { RankingTimeline } from './RankingTimeline';
import { RankingScoreCard } from './RankingScoreCard';
import { AddKeywordModal, type KeywordFormData } from './AddKeywordModal';

interface GBPProfile {
  id: number;
  location_name: string;
  address: string;
}

/** Only the bits of the org profile the grid needs. */
interface OrganizationCenter {
  name?: string;
  // DRF renders the org's DecimalField coordinates as strings.
  latitude?: string | number | null;
  longitude?: string | number | null;
}

// Where the grid lands for an org that has never saved its coordinates.
// Settings → Organization geocodes the office address, so this is a floor,
// not the expected case.
const FALLBACK_LAT = 29.9947;
const FALLBACK_LNG = -95.5736;

const toCoordinate = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const RankingHeatmapTab: React.FC = () => {
  const [selectedKeywordId, setSelectedKeywordId] = useState<number | null>(null);
  const [selectedScanId, setSelectedScanId] = useState<number | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const queryClient = useQueryClient();

  // Fetch ranking keywords
  const keywordsQuery = useQuery({
    queryKey: ['ranking-keywords'],
    queryFn: async () => {
      const response = await api.get<RankingKeyword[] | { results: RankingKeyword[] }>(
        '/api/marketing/ranking-keywords/'
      );
      const data = response.data;
      if (Array.isArray(data)) return data;
      if (data && 'results' in data) return data.results;
      return [];
    },
    refetchInterval: query => query.state.data?.some(keyword => ['pending','in_progress'].includes(keyword.latest_scan?.status || '')) ? 5000 : false,
  });

  const { data: keywords = [], isLoading: keywordsLoading } = keywordsQuery;

  // Auto-select first keyword
  const selectedKeyword = keywords.find((k) => k.id === selectedKeywordId) || keywords[0] || null;
  const effectiveKeywordId = selectedKeyword?.id ?? null;

  // Fetch scans for selected keyword
  const scansQuery = useQuery({
    queryKey: ['ranking-scans', effectiveKeywordId],
    queryFn: async () => {
      if (!effectiveKeywordId) return [];
      const response = await api.get<RankingScanSummary[]>(
        `/api/marketing/ranking-keywords/${effectiveKeywordId}/scans/`
      );
      return response.data || [];
    },
    enabled: !!effectiveKeywordId,
  });

  const {data: scans = []} = scansQuery;

  // Auto-select latest scan
  const effectiveScanId = selectedScanId && scans.some((s) => s.id === selectedScanId)
    ? selectedScanId
    : scans[0]?.id ?? null;

  // Fetch full scan detail
  const scanQuery = useQuery({
    queryKey: ['ranking-scan-detail', effectiveScanId],
    queryFn: async () => {
      if (!effectiveScanId) return null;
      const response = await api.get<RankingScanDetail>(
        `/api/marketing/ranking-scans/${effectiveScanId}/`
      );
      return response.data;
    },
    enabled: !!effectiveScanId,
  });

  const {data: scanDetail, isLoading: scanLoading} = scanQuery;

  // Check for pending scans - poll
  const hasPendingScan = selectedKeyword?.latest_scan?.status === 'pending'
    || selectedKeyword?.latest_scan?.status === 'in_progress';

  useEffect(() => {
    if (selectedKeyword?.latest_scan?.status === 'completed') {
      queryClient.invalidateQueries({queryKey:['ranking-scans', effectiveKeywordId]});
      queryClient.invalidateQueries({queryKey:['local-seo-stats']});
    }
  }, [selectedKeyword?.latest_scan?.id, selectedKeyword?.latest_scan?.status, effectiveKeywordId, queryClient]);

  // The org's own office anchors the grid and names the business to match
  const { data: organization } = useQuery({
    queryKey: ['organization-current'],
    queryFn: async () => {
      const response = await api.get<OrganizationCenter>('/api/organizations/current/');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Get GBP profile for default center
  const { data: gbpProfiles = [] } = useQuery({
    queryKey: ['gbp-profiles'],
    queryFn: async () => {
      return (await api.getAllPages<GBPProfile>('/api/marketing/gbp-profiles/')).data;
    },
  });

  // Mutations
  const createKeywordMutation = useMutation({
    mutationFn: async (data: KeywordFormData) => {
      const response = await api.post('/api/marketing/ranking-keywords/', data);
      if (response.status >= 400) {
        const errData = response.data as any;
        throw new Error(
          errData?.non_field_errors?.[0] || errData?.detail || 'Failed to create keyword'
        );
      }
      return response.data as RankingKeyword;
    },
    onSuccess: (newKeyword) => {
      queryClient.invalidateQueries({ queryKey: ['ranking-keywords'] });
      queryClient.invalidateQueries({ queryKey: ['local-seo-stats'] });
      // Auto-trigger first scan
      if (newKeyword?.id) {
        triggerScanMutation.mutate(newKeyword.id);
      }
    },
  });

  const deleteKeywordMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/marketing/ranking-keywords/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ranking-keywords'] });
      queryClient.invalidateQueries({ queryKey: ['local-seo-stats'] });
      if (selectedKeywordId === showDeleteConfirm) {
        setSelectedKeywordId(null);
        setSelectedScanId(null);
      }
      setShowDeleteConfirm(null);
    },
  });

  const triggerScanMutation = useMutation({
    mutationFn: async (keywordId: number) => {
      const response = await api.post(`/api/marketing/ranking-keywords/${keywordId}/scan/`, {});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ranking-keywords'] });
      queryClient.invalidateQueries({ queryKey: ['local-seo-stats'] });
      queryClient.invalidateQueries({ queryKey: ['ranking-scans', effectiveKeywordId] });
    },
  });

  const handleAddKeyword = useCallback(async (data: KeywordFormData) => {
    await createKeywordMutation.mutateAsync(data);
  }, [createKeywordMutation]);

  const handleDeleteKeyword = useCallback((id: number) => {
    setShowDeleteConfirm(id);
  }, []);

  const confirmDelete = useCallback(() => {
    if (showDeleteConfirm) {
      deleteKeywordMutation.mutate(showDeleteConfirm);
    }
  }, [showDeleteConfirm, deleteKeywordMutation]);

  // Find previous scan for trend calculation
  const currentScanIndex = scans.findIndex((s) => s.id === effectiveScanId);
  const previousScan = currentScanIndex >= 0 && currentScanIndex < scans.length - 1
    ? scans[currentScanIndex + 1]
    : null;

  // Centre on the office the org already recorded. The old literals were
  // Houston, pre-filled into a required field: not blank, just wrong, and the
  // user had to notice before overwriting them.
  const defaultLat = toCoordinate(organization?.latitude) ?? FALLBACK_LAT;
  const defaultLng = toCoordinate(organization?.longitude) ?? FALLBACK_LNG;

  if (keywordsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {[['keywords',keywordsQuery],['scan history',scansQuery],['scan detail',scanQuery]].map(([label, result]) => {
        const query = result as typeof keywordsQuery;
        return query.isError ? <Alert key={label as string} color="danger" sx={{mb:2}} endDecorator={<Button size="sm" onClick={() => query.refetch()}>Retry {label as string}</Button>}>Unable to load {label as string}.</Alert> : null;
      })}
      {triggerScanMutation.isError && <Alert color="danger" sx={{mb:2}}>{localSEOError(triggerScanMutation.error,'Unable to start the scan. Please try again.')}</Alert>}
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography level="h4">Keyword Ranking Heatmap</Typography>
      </Box>

      {/* Keyword selector */}
      <Box sx={{ mb: 2 }}>
        <KeywordSelector
          keywords={keywords}
          selectedKeywordId={effectiveKeywordId}
          onSelect={(id) => {
            setSelectedKeywordId(id);
            setSelectedScanId(null);
          }}
          onAdd={() => setShowAddModal(true)}
          onDelete={handleDeleteKeyword}
        />
      </Box>

      {keywordsQuery.isError ? null : keywords.length === 0 ? (
        <Alert color="neutral" sx={{ mt: 2 }}>
          No ranking keywords configured yet. Add up to 3 keywords to track your local search
          rankings across a geographic grid.
        </Alert>
      ) : (
        <Stack spacing={2}>
          <Stack direction={{xs:'column',sm:'row'}} justifyContent="space-between" gap={1}>
            <Box><Typography level="title-md">{selectedKeyword?.keyword}</Typography><Chip color={selectedKeyword?.latest_scan?.status === 'failed' ? 'danger' : 'neutral'}>{selectedKeyword?.latest_scan?.status?.replace('_',' ') || 'Not scanned yet'}</Chip></Box>
            <Button loading={triggerScanMutation.isPending} disabled={hasPendingScan || !selectedKeyword?.is_active} onClick={() => effectiveKeywordId && triggerScanMutation.mutate(effectiveKeywordId)}>{selectedKeyword?.latest_scan?.status === 'failed' ? 'Retry scan' : 'Scan now'}</Button>
          </Stack>
          {hasPendingScan && <Alert color="primary">Scan in progress. Results refresh automatically; the map shows the last completed scan until the new one is ready.</Alert>}
          {selectedKeyword?.latest_scan?.status === 'failed' && <Alert color="danger">{selectedKeyword.latest_scan.error_message || 'The latest scan failed. Retry when ready.'}</Alert>}
          {!hasPendingScan && !scansQuery.isPending && !scansQuery.isError && scans.length === 0 && <Alert color="neutral">No completed scans yet. Run a scan to populate the map.</Alert>}
          {/* Map */}
          {scanDetail && <RankingMap
            scan={scanDetail ?? null}
            centerLat={selectedKeyword?.center_lat ?? defaultLat}
            centerLng={selectedKeyword?.center_lng ?? defaultLng}
            loading={scanLoading}
          />}

          {/* Timeline */}
          {scans.length > 0 && <RankingTimeline
            scans={scans}
            selectedScanId={effectiveScanId}
            onSelect={setSelectedScanId}
          />}

          {/* Score card */}
          {scanDetail && (
            <RankingScoreCard scan={scanDetail} previousScan={previousScan} />
          )}
        </Stack>
      )}

      {/* Add Keyword Modal */}
      <AddKeywordModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddKeyword}
        defaultCenterLat={defaultLat}
        defaultCenterLng={defaultLng}
        defaultBusinessName={gbpProfiles[0]?.location_name || organization?.name}
      />

      {/* Delete Confirmation Modal */}
      <Modal open={showDeleteConfirm !== null} onClose={() => setShowDeleteConfirm(null)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">Delete Keyword</Typography>
          {deleteKeywordMutation.isError && <Alert color="danger">{localSEOError(deleteKeywordMutation.error,'Unable to delete this keyword.')}</Alert>}
          <Typography level="body-md" sx={{ my: 1 }}>
            Are you sure you want to delete this keyword and all its scan history? This cannot be
            undone.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="outlined" color="neutral" onClick={() => setShowDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={confirmDelete}
              loading={deleteKeywordMutation.isPending}
            >
              Delete
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
};
